<template>
    <ac-modal :title="'Edit Coverage - ' + fullName" ref="modalRef">
        <template>
            <form v-if="widgetReady">
                <div class="row">
                    <div class="info-board info-board-blue" style="margin: 0 0 20px 0">
                        <p>The primary member is responsible for managing their profile.</p>
                        <p style="font-weight: bold !important;">
                            Only one future starting coverage change is allowed per member.
                        </p>
                    </div>
                    <div v-if="currentCoverage" class="alert alert-info col-lg-12">
                        <span>{{ currentCoverage }}</span>
                    </div>
                    <div v-if="futureCoverage" class="alert alert-warning col-lg-12">
                        <span>{{ futureCoverage }}</span>
                    </div>
                </div>

                <div class="row">
                    <div class="col-sm-4">
                        <ac-coverage-drop-down v-model="vm.coverageLevelId"/>
                    </div>
                    <div class="col-sm-4">
                        <label>Number of Dependents</label>
                        <input type="text" class="form-control" name="numberOfChildren" value=""
                               v-model="vm.numberOfChildren" :class="{error:showTermMessage || showDependentList }"/>
                    </div>
                    <div class="col-sm-4">
                        <div class="form-group">
                            <label>Effective On</label><br/>
                            <ac-date-picker v-if="!futureCoverage" v-model="vm.effectiveDate"></ac-date-picker>
                            <input v-else type="text" class="form-control" :disabled="true" v-model="vm.effectiveDate" />
                        </div>
                    </div>
                </div>

                <div class="row" v-if="(showDependentList || showTermMessage) && children.length">
                    <div class="row info-board info-board-red" style="border-bottom-width: 1px;">
                        <p>
                            To terminate registered dependents you will need to reduce the number
                            of dependents above to reflect the new desired coverage level, 
                            and also select which dependents should be removed:
                        </p>
                        <div class="col-lg-12" v-for="c in children">
                            <input type="checkbox" class="form-control" 
                                v-model="c.isSelected" :disabled="!showDependentList"
                                :id="'dependent-to-terminate-'+c.consumerId">
                            <label :for="'dependent-to-terminate-'+c.consumerId">{{ c.name }}</label>
                        </div>
                    </div>
                </div>
            </form>
        </template>

        <template slot="buttons">
            <ac-button v-if="children.length" class="pull-left btn btn-warning btn-sm" :action="toggleTerminations">
                {{showTermMessage ?"CLOSE TERMINATIONS" : "EDIT TERMINATIONS"}}
            </ac-button>
            <ac-button class="btn btn-red btn-sm" :action="closeModal">
                CANCEL
            </ac-button>
            <ac-button class="btn btn-green btn-sm" :action="updateConsumer">
                SAVE CHANGES FOR MEMBER
            </ac-button>
        </template>
    </ac-modal>
</template>

<script>
import {api} from "../../../global/framework/api.js";
import {configTables} from "../../../global/configTables.js";
import _ from "lodash/lodash.js";
import {notifications} from "../../../global/notifications.js";
import {common} from "../../../global/common.js";
import AcCoverageDropDown from "../../../components/custom/AcCoverageDropDown.vue";
import {formatter} from "../../../global/formatter.js";

export default {
    name: "EditEmployeeCoverageModal",
    components: {AcCoverageDropDown},
    data() {
        return {
            widgetReady: false,
            vm: {},
            originalNumberOfChildren: 0,
            children: [],
            coverageTypes: [],
            showTermMessage: false
        }
    },
    async mounted() {
        this.coverageTypes = await configTables.getCoverageOptions();
        //console.log("this.coverageTypes: ", this.coverageTypes);
    },
    computed: {
        showDependentList() {
            let numberOfChildren = this.vm.numberOfChildren;
            let numberOfRegisteredChildren = this.children.length;

            let show = numberOfChildren > 0 &&
                numberOfRegisteredChildren > 0 &&
                this.childrenToRemove > 0 &&
                numberOfChildren < numberOfRegisteredChildren;
            if (!show) {
                // if we don't allow selecting dependents, then we want to clear out the selection to avoid accidental terms.
                for (let i = 0; i < this.children.length; i++) {
                    this.children[i].isSelected = false;
                }
            }

            return show;
        },
        childrenToRemove() {
            let numChildren = parseInt(this.vm.numberOfChildren || 0);
            return numChildren < this.originalNumberOfChildren ? this.originalNumberOfChildren - numChildren : 0;
        },
        currentCoverage() {
            let message = this.coverageMessage(this.vm.coverage);
            return message;
        },
        futureCoverage() {
            let message = this.coverageMessage(this.vm.future);
            return message;
        },
        fullName() {
            return this.vm.contactInfo ? this.vm.contactInfo.firstName + " " + this.vm.contactInfo.lastName : "";
        },
    },
    methods: {
        async showModal({companyId, consumerId}) {
            let mainVm = await api.getDataWithTwoIds("Company", companyId, "GetEditEmployeeModel", consumerId);
            // console.log("EditEmployeeCoverageModal showModal mainVm: ", mainVm);
            mainVm.effectiveDate = (mainVm.future && mainVm.future.consumerCoverageId > 0) 
                ? new Date(mainVm.future.effectiveDate).toLocaleDateString() : formatter.getDateForDatePicker();

            this.mapData('vm', mainVm);
            this.originalNumberOfChildren = parseInt(mainVm.numberOfChildren || 0);
            this.mapChildren(companyId, consumerId);
            this.widgetReady = true;
            this.$refs.modalRef.open();
        },
        toggleTerminations() {
            this.showTermMessage = !this.showTermMessage;
        },
        closeModal() {
            this.$refs.modalRef.close();
        },
        async updateConsumer() {
            let selection = [];
            for (let i = 0; i < this.children.length; i++) {
                if (this.children[i].isSelected) {
                    selection.push(this.children[i].consumerId);
                }
            }
            let remainingKids = this.children.length - selection.length;
            if (this.showDependentList && remainingKids !== (parseInt(this.vm.numberOfChildren || 0))) {
                notifications.show(
                    {title: "Error!", message: "Please verify the number of dependents to be removed"},
                    common.ACRESSA_NOTIFICATIONS.ERROR);
                return false;
            }
            this.vm.membersToBeRemoved = selection;
            let result = await api.post("Company", "UpdateEmployee", this.vm);
            if (result) {
                this.$bus.$emit(this.$AcEvents.EMPLOYEE_UPDATED, result);
                this.closeModal();
            }
        },
        coverageMessage(coverageLevel) {
            if (!Boolean(coverageLevel) || !Boolean(coverageLevel.coverageLevelId)) {
                return null;
            }
            let coverageItem = _.find(this.coverageTypes, (current) => {
                return current.coverageLevelId == coverageLevel.coverageLevelId;
            });
            let coverage = coverageItem.text;
            let currCov = "Member has '"
                + coverage + "' coverage with "
                + coverageLevel.numberOfChildren + " dependent" + (coverageLevel.numberOfChildren == 1 ? " " : "s ")
                + " beginning " + new Date(coverageLevel.effectiveDate).toDateString() + ".";
            return currCov;
        },
        mapChildren(companyId, consumerId) {
            let self = this;
            $.ajax({
                type: "GET",
                url: "Company/" + companyId + "/GetConsumerChildren/" + consumerId,
                success: function (result) {
                    for (let i = 0; i < result.length; i++) {
                        result[i].isSelected = false;
                    }
                    self.children = result;
                    //console.log("result children count:" + result.length);
                }
            });
        }
    }

}
</script>

<style scoped>

</style>